// Onboarding state and pure derivations (DOMAIN_AUTHORIZATION_SPEC.md). No
// timers, DOM, or network here: actions.ts talks to the backend and writes its
// answers into this state; components render from it.

import type { ContainerPhase, ContainerView, StageName } from './api.ts';
import { normalizeDomain, validateOrganization } from './domain.ts';
import { RECORD_NAME, type Provider } from './provider.ts';

export type Step = '1' | 'check' | '2' | '3' | '4';
export const STEPS: readonly Step[] = ['1', 'check', '2', '3', '4'];

export type CheckStatus = 'idle' | 'running' | 'complete' | 'missing' | 'timeout' | 'stopped';
export type WorkspaceStatus = 'idle' | 'creating' | 'ready' | 'failed';

/** Terminal glyphs (spec §4.4). */
export type Glyph = '›' | '‹' | '✓' | '·' | '!' | '✕' | '■';

export interface LogLine {
  id: string;
  time: string;
  glyph: Glyph;
  text: string;
  duration?: string;
  /** Domain-check sub-step (0–3) the line belongs to. */
  group?: number;
}

export interface ConsentRecord {
  user: string;
  domain: string;
  recordName: string;
  time: string;
  statementVersion: string;
}

/** Values the backend owns for the current onboarding session. */
export interface ServerData {
  sessionId: string | null;
  workspaceId: string | null;
  /** A record already used traceira-authz.<domain> during the domain check. */
  nameInUse: boolean;
  /** The last request failed with this message. */
  error: string | null;
  busy: boolean;
}

export const CONTAINER_STAGE_NAMES: readonly StageName[] = [
  'requested',
  'scheduled',
  'storage_ready',
  'image_ready',
  'started',
  'running',
];

export const CONTAINER_STAGE_LABELS: Record<StageName, string> = {
  requested: 'Workspace resources created',
  scheduled: 'Placed on a server',
  storage_ready: 'Workspace storage attached',
  image_ready: 'Kali Linux image ready',
  started: 'Container started',
  running: 'Workspace container running',
};

export function inFlightContainerView(): ContainerView {
  return {
    phase: 'creating',
    stages: CONTAINER_STAGE_NAMES.map((name, i) => ({
      name,
      label: CONTAINER_STAGE_LABELS[name],
      status: i === 0 ? 'active' : 'pending',
      at: null,
    })),
    attempt: null,
    createdAt: null,
    readyDeadline: null,
    expiresAt: null,
    observedAt: null,
    stale: false,
    notice: null,
    canStart: false,
    pollAfterMs: 3000,
  };
}

export function isContainerTerminal(phase: ContainerPhase | undefined | null): boolean {
  return (
    phase === 'running' || phase === 'failed' || phase === 'removed' || phase === 'unavailable'
  );
}

export function canContinueFromCheck(state: OnboardingState): boolean {
  return state.check === 'complete' && isContainerTerminal(state.container?.phase);
}

export interface OnboardingState {
  server: ServerData;
  /**
   * The organization a signed-in person adds this workspace to: an id, `null` for a new one, or
   * unset until step 1 offers the choice (anonymous setup never does).
   */
  organizationId?: string | null;
  organization: string;
  typedDomain: string;
  authorizerName: string;
  authorizerEmail: string;
  /** Answer to the subdomain offer for the current typed value. */
  subdomainChoice: 'parent' | 'keep' | null;
  errors: {
    organization?: string;
    domain?: string;
    authorizerName?: string;
    authorizerEmail?: string;
  };
  /** The domain being verified; empty until the check first starts. */
  domain: string;
  provider: Provider | null;
  check: CheckStatus;
  checkLines: LogLine[];
  container: ContainerView | null;
  consent: ConsentRecord | null;
  /** Consent is shown optimistically while its backend write is pending. */
  consentSaving: boolean;
  workspace: WorkspaceStatus;
  creationAttempts: number;
}

export function emptyServerData(): ServerData {
  return {
    sessionId: null,
    workspaceId: null,
    nameInUse: false,
    error: null,
    busy: false,
  };
}

export function initialState(): OnboardingState {
  return {
    server: emptyServerData(),
    organization: '',
    typedDomain: '',
    authorizerName: '',
    authorizerEmail: '',
    subdomainChoice: null,
    errors: {},
    domain: '',
    provider: null,
    check: 'idle',
    checkLines: [],
    container: null,
    consent: null,
    consentSaving: false,
    workspace: 'idle',
    creationAttempts: 0,
  };
}

// ---------------------------------------------------------------------------
// Step 1
// ---------------------------------------------------------------------------

export interface Step1Result {
  state: OnboardingState;
  valid: boolean;
  domain: string | null;
}

/** The domain that will be verified for the current input, honoring the subdomain choice. */
export function resolvedDomain(state: OnboardingState): string | null {
  const result = normalizeDomain(state.typedDomain);
  if (!result.ok) return null;
  return result.parent && state.subdomainChoice === 'parent' ? result.parent : result.domain;
}

export function validateStep1(state: OnboardingState): Step1Result {
  const organization = validateOrganization(state.organization);
  const domainResult = normalizeDomain(state.typedDomain);
  const errors: OnboardingState['errors'] = {};
  if (organization) errors.organization = organization;
  if (!domainResult.ok) errors.domain = domainResult.error;
  const name = state.authorizerName.trim();
  const email = state.authorizerEmail.trim().toLowerCase();
  const verifiedDomain = resolvedDomain(state);
  if (name.length < 2 || name.length > 100 || /[\r\n]/.test(name))
    errors.authorizerName = 'Enter the domain authorizer’s full name (2–100 characters).';
  if (email.length > 254 || !/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9.-]+$/.test(email))
    errors.authorizerEmail = 'Enter a valid email address on the domain you are verifying.';
  else if (verifiedDomain && email.split('@')[1] !== verifiedDomain)
    errors.authorizerEmail = `Use an email address at @${verifiedDomain}.`;
  const valid = Object.keys(errors).length === 0;
  return {
    state: {
      ...state,
      organization: state.organization.trim(),
      authorizerName: name,
      authorizerEmail: email,
      errors,
    },
    valid,
    domain: valid ? resolvedDomain(state) : null,
  };
}

export function setTypedDomain(state: OnboardingState, typedDomain: string): OnboardingState {
  const errors = { ...state.errors };
  delete errors.domain;
  delete errors.authorizerEmail;
  return { ...state, typedDomain, subdomainChoice: null, errors };
}

export function setOrganization(state: OnboardingState, organization: string): OnboardingState {
  const errors = { ...state.errors };
  delete errors.organization;
  return { ...state, organization, errors };
}

export function setAuthorizer(
  state: OnboardingState,
  field: 'authorizerName' | 'authorizerEmail',
  value: string,
): OnboardingState {
  const errors = { ...state.errors };
  if (field === 'authorizerName') delete errors.authorizerName;
  else delete errors.authorizerEmail;
  return { ...state, [field]: value, errors };
}

export function chooseSubdomain(
  state: OnboardingState,
  choice: 'parent' | 'keep',
): OnboardingState {
  const errors = { ...state.errors };
  delete errors.authorizerEmail;
  return { ...state, subdomainChoice: choice, errors };
}

// ---------------------------------------------------------------------------
// Domain check (spec §4)
// ---------------------------------------------------------------------------

export type SubStepStatus = 'pending' | 'running' | 'done' | 'attention' | 'failed' | 'stopped';
export interface SubStep {
  label: string;
  short: string;
  status: SubStepStatus;
  detail: string;
}

const SUBSTEP_LABELS = [
  ['Find your DNS servers', 'DNS servers'],
  ['Identify your provider', 'Provider'],
  ['Check the record name', 'Record name'],
  ['Prepare your instructions', 'Instructions'],
] as const;

/** The four sub-steps derived from the lines written so far (spec §4.3, §4.5). */
export function checkSubSteps(state: OnboardingState): SubStep[] {
  const p = state.provider;
  // A sub-step is done once a later sub-step has written a line (or the check completed).
  const lastGroup = Math.max(-1, ...state.checkLines.map((l) => l.group ?? 0));

  return SUBSTEP_LABELS.map(([label, short], g) => {
    let status: SubStepStatus = 'pending';
    let detail = 'Not started';
    const isCurrent = g === Math.max(lastGroup, 0);
    if (state.check === 'complete' || g < lastGroup) {
      status = 'done';
      if (g === 0) {
        const answer = state.checkLines.find((l) => l.group === 0 && l.glyph === '‹');
        const count = p?.nameservers.length ?? 0;
        detail = `${count} nameservers${answer?.duration ? ` · ${answer.duration}` : ''}`;
      }
      if (g === 1) {
        detail = p?.kind === 'unknown' ? 'Provider not recognized' : (p?.name ?? '');
        if (p?.kind === 'unknown') status = 'attention';
      }
      if (g === 2) {
        const conflict = state.server.nameInUse;
        detail = conflict
          ? 'A record already uses this name'
          : `Free · ${RECORD_NAME} isn't in use`;
        if (conflict) status = 'attention';
      }
      if (g === 3)
        detail =
          p?.kind === 'unknown' ? 'Ready for your DNS provider' : `Ready for ${p?.shortName ?? ''}`;
    } else if (isCurrent && state.check === 'running') {
      status = 'running';
      detail = 'Checking…';
    } else if (isCurrent && state.check === 'stopped') {
      status = 'stopped';
      detail = 'Stopped';
    } else if (isCurrent && state.check === 'timeout') {
      status = 'failed';
      detail = 'No answer';
    } else if (isCurrent && state.check === 'missing') {
      status = 'failed';
      detail = 'No DNS servers found';
    }
    return { label, short, status, detail };
  });
}

// ---------------------------------------------------------------------------
// Step 2 · record checks (spec §5.6)
// ---------------------------------------------------------------------------

/** The domain check can move on once it finished and the backend has the consent. */
export function canContinue(state: OnboardingState): boolean {
  return (
    state.check === 'complete' && Boolean(state.consent?.statementVersion) && !state.consentSaving
  );
}

// ---------------------------------------------------------------------------
// Step 3 (spec §6)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Routing (spec §2.2): deep links fall back to the earliest incomplete step
// ---------------------------------------------------------------------------

export function resolveStep(state: OnboardingState, requested: string | undefined): Step {
  const step = (STEPS as readonly string[]).includes(requested ?? '') ? (requested as Step) : '1';
  if ((step === '3' || step === '4') && state.workspace === 'ready') return step;
  if (step === '2' || step === '3' || step === '4') {
    if (state.workspace !== 'idle') return '2';
    return state.domain ? 'check' : '1';
  }
  if (step === 'check') return state.domain ? 'check' : '1';
  return '1';
}
